import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { chatShow } from '@/lib/components/SupportCard';
import { UserProfile } from '@/lib/components/UserProfile';
import { UserProfileProps } from '@/lib/components/UserProfile/UserProfile';
import { ChatIcon, EmblueIcon } from '@/lib/icon';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import { useVersion } from '@/src/application/hooks/useVersion';
import { getEnv } from '@/src/application/hooks/util/useEnv';
import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';

import { RouteEnum } from './routeEnum';

import './TopBar.tailwind.css';

const TopBarLink = ({ path, children }: { path: string; children: string }) => {
  const { version } = useVersion();
  const { pathname } = useLocation();

  useEffect(() => {
    document.body.setAttribute('data-tab', pathname);
  }, [pathname]);

  let link = (version === 'v2' && path !== RouteEnum.Home ? '/v2' : '') + path;
  if (link === '') {
    link = '/v2';
  }
  return (
    <div
      className={`h-full px-2 hover:bg-[#3170C9] 2xl:px-4 ${
        (path === RouteEnum.Home && pathname === '/v2') ||
        (path !== RouteEnum.Home && pathname.replace('/v2', '').includes(path))
          ? 'bg-gradient-hover border-b-[0.125rem] border-b-white pt-0.5'
          : ''
      }`}
    >
      <NavLink className="h-full w-full" to={link}>
        <Flex alignment="center">
          {(path === RouteEnum.Home && pathname === '/v2') ||
          (path !== RouteEnum.Home && pathname.replace('/v2', '').includes(path)) ? (
            <b>{children}</b>
          ) : (
            children
          )}
        </Flex>
      </NavLink>
    </div>
  );
};

export const TopBar = ({ name, email, accountId, companyName }: UserProfileProps) => {
  const { t } = useTranslation();
  const showNpsModule = useFeatureFlag('showNpsModule');

  /** cspell:disable */
  const cookiePreProd = document.cookie
    .split('; ')
    .find((row) => row.startsWith('EMA-APP-PREPROD='))
    ?.split('=')[1];
  /** cspell:enable */

  let background = 'bg-emblue-primary';

  if (getEnv() === 'production' && cookiePreProd === '1') {
    background = 'bg-emblue-success';
  }

  if (getEnv() === 'staging') {
    background = 'bg-emblue-sleepy';
  }

  return (
    <div
      className={`top-bar h-16 w-full text-white md:text-12 lg:text-14 2xl:text-16 ${background}`}
    >
      <Flex>
        <div className="h-full w-7/12 px-8 lg:w-8/12 2xl:w-1/2">
          <div className="flex h-full items-center">
            <EmblueIcon />
            <TopBarLink path={RouteEnum.Home}>{t('TOP_BAR.home')}</TopBarLink>
            <TopBarLink path={RouteEnum.Dashboard}>{t('TOP_BAR.dashboard')}</TopBarLink>
            <TopBarLink path={RouteEnum.Contacts}>{t('TOP_BAR.contacts')}</TopBarLink>
            <TopBarLink path={RouteEnum.Campaigns}>{t('TOP_BAR.campaigns')}</TopBarLink>
            <TopBarLink path={RouteEnum.Reports}>{t('TOP_BAR.reports')}</TopBarLink>
            <TopBarLink path={RouteEnum.Automation}>{t('TOP_BAR.automation')}</TopBarLink>
            <TopBarLink path={RouteEnum.OnSite}>{t('TOP_BAR.on_site')}</TopBarLink>
            {showNpsModule && <TopBarLink path={RouteEnum.Nps}>{t('TOP_BAR.nps')}</TopBarLink>}
          </div>
        </div>
        <Flex noGrow withGap alignment="end" gapSize="medium">
          <button onClick={chatShow}>
            <ChatIcon />
          </button>
          <div className="flex h-full min-w-max pr-8">
            <UserProfile
              dropDown
              accountId={accountId}
              companyName={companyName}
              email={email}
              name={name}
            />
          </div>
        </Flex>
      </Flex>
    </div>
  );
};
